import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function PrivacyPage() {
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    document.title = 'Privacy Policy | VE Goodies Vending Services DFW';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Privacy Policy for VE Goodies. Learn how we collect, use, and protect your information when using our vending machine services in Carrollton and across the Dallas-Fort Worth metroplex.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/privacy`;

    // WebPage Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-privacy';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy – VE Goodies",
      "description": "Privacy Policy for VE Goodies vending machine services in Carrollton and across the Dallas-Fort Worth metroplex. Learn how we protect your data and privacy.",
      "url": `${SITE_URL}/privacy`,
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "VE Goodies",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "VE Goodies",
        "telephone": "+14699223413",
        "email": "vegoodiesllc@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Carrollton",
          "addressRegion": "TX",
          "addressCountry": "US"
        }
      },
      "dateModified": today
    });
    document.head.appendChild(script);

    // BreadcrumbList Schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'schema-breadcrumb';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Privacy Policy",
          "item": `${SITE_URL}/privacy`
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.getElementById('schema-privacy')?.remove();
      document.getElementById('schema-breadcrumb')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 mb-12">
            Last Updated: January 2025
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="mb-4">
                VE Goodies ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our vending machine services and visit our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="mb-4">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Contact Information:</strong> Name, email address, phone number, business address</li>
                <li><strong>Business Information:</strong> Company name, industry, number of employees</li>
                <li><strong>Service Preferences:</strong> Product preferences, dietary requirements, service needs</li>
                <li><strong>Transaction Data:</strong> Purchase history, payment information (processed securely)</li>
                <li><strong>Communication Records:</strong> Correspondence, service requests, feedback</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Provide, maintain, and improve our vending services</li>
                <li>Process and fulfill service requests</li>
                <li>Communicate with you about services, updates, and promotions</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Analyze usage patterns to enhance service quality</li>
                <li>Comply with legal obligations and protect our rights</li>
                <li>Prevent fraud and ensure security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="mb-4">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operations (payment processors, maintenance partners)</li>
                <li><strong>Business Partners:</strong> Product suppliers and distributors as necessary for service delivery</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="mb-4">
                We retain your information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. When information is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Privacy Rights</h2>
              <p className="mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Access and receive a copy of your personal information</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Request deletion of your information (subject to legal obligations)</li>
                <li>Opt-out of marketing communications</li>
                <li>Object to certain processing of your information</li>
              </ul>
              <p className="mb-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="mb-4">
                Our website may use cookies and similar tracking technologies to enhance user experience, analyze site traffic, and understand user preferences. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
              <p className="mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="mb-4">
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware of such collection, we will take steps to delete the information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website with a new "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 space-y-2">
                <p><strong>Email:</strong> vegoodiesllc@gmail.com</p>
                <p><strong>Phone:</strong> (469) 922-3413</p>
                <p><strong>Service Area:</strong> Dallas-Fort Worth Metroplex, TX</p>
              </div>
            </section>

            <section className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Consent</h2>
              <p className="mb-4">
                By using our services and website, you consent to the collection and use of information as described in this Privacy Policy. If you do not agree with this policy, please do not use our services.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}